import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  BookingService,
  CustomerService,
  EmployeeService,
  getSecondsFromDateTime,
  InsuranceService,
  ServiceService,
  timeStrToSeconds,
  unixToDateTime,
  User,
} from '@beautyapp/core';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { takeUntil } from 'rxjs/operators';

import {
  AddEmployeeAppointment,
  EditEmployeeAppointment,
  SetEmployeeAppointment,
} from '../../appointment.actions';
import { AddEmployeeAppointmentUserComponent } from '../add-employee-appointment-user/add-employee-appointment-user.component';
import { TranslatePipe } from '@ngx-translate/core';
import { NzContentComponent, NzLayoutComponent } from 'ng-zorro-antd/layout';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NgxsFormDirective } from '@ngxs/form-plugin';
import { DatepickerComponent, SelectSearchComponent, TextareaComponent, TimePickerComponent } from '@beautyapp/form';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzInputGroupComponent, NzInputModule } from 'ng-zorro-antd/input';
import { NzCheckboxComponent } from 'ng-zorro-antd/checkbox';

@Component({
  selector: 'beautyapp-add-empoyee-mobile-appointment',
  templateUrl: './add-empoyee-mobile-appointment.component.html',
  styleUrls: ['./add-empoyee-mobile-appointment.component.scss'],
  standalone: true,
  imports: [
    TranslatePipe,
    NzLayoutComponent,
    NzContentComponent,
    NzGridModule,
    NzFormModule,
    FormsModule,
    ReactiveFormsModule,
    NgxsFormDirective,
    SelectSearchComponent,
    NzButtonComponent,
    NzIconDirective,
    NzInputModule,
    DatepickerComponent,
    TimePickerComponent,
    TextareaComponent,
    NzCheckboxComponent,
    NzModalFooterDirective
  ]
})
export class AddEmpoyeeMobileAppointmentComponent implements OnInit, OnDestroy {
  private fb = inject(UntypedFormBuilder);
  private serviceService = inject(ServiceService);
  private bookingService = inject(BookingService);
  private customerService = inject(CustomerService);
  private employeeService = inject(EmployeeService);
  private insuranceService = inject(InsuranceService);
  private modalService = inject(NzModalService);
  modal = inject(NzModalRef);
  store = inject(Store);

  readonly data = inject(NZ_MODAL_DATA);

  get bookingId() {
    return this.data.bookingId;
  }

  get date() {
    return this.data.date;
  }

  form!: UntypedFormGroup;
  customers: User[] = [];
  services: any[] = [];
  selectedCustomer?: User;
  selectedService: any;
  sub$: Subject<any> = new Subject<any>();

  constructor() {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
  }

  ngOnInit(): void {
    if (this.bookingId) {
      this.store.dispatch(new SetEmployeeAppointment(this.bookingId));
    } else {
      this.form.patchValue({
        bookingDate: new Date(this.date).toISOString(),
        startTime: new Date(this.date).toISOString(),
      });
    }

    this.form
      .get('userId')
      ?.valueChanges.pipe(takeUntil(this.sub$))
      .subscribe((item) => {
        if (item) {
          this.getCustomerById();
        }
      });
    this.form
      .get('serviceId')
      ?.valueChanges.pipe(takeUntil(this.sub$))
      .subscribe((item) => {
        if (item) {
          this.getServiceById();
        }
      });
  }

  initForm() {
    this.form = this.fb.group({
      id: null,
      userId: [null, Validators.required],
      status: [1, Validators.required],
      serviceId: [null, Validators.required],
      bookingDate: [null, Validators.required],
      startTime: [null, Validators.required],
      endTime: [null, Validators.required],
      sendNotification: true,
      description: null,
    });
  }

  searchCustomer(search: any) {
    this.customerService
      .search(search)
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.customers = res;
      });
  }

  getCustomerById() {
    this.customerService
      .getById(this.form.get('userId')?.value)
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.selectedCustomer = res;
      });
  }

  searchService(search: any) {
    this.serviceService
      .search(search)
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        this.services = res;
      });
  }

  getServiceById() {
    this.serviceService
      .getById(this.form.get('serviceId')?.value)
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        if (res) {
          this.selectedService = res;
          const startSeconds = getSecondsFromDateTime(
            this.form.get('startTime')?.value
          );
          this.form
            .get('endTime')
            ?.setValue(
              unixToDateTime(startSeconds + timeStrToSeconds(res.time) * 60)
            );
        }
      });
  }

  addNewuser() {
    const modal = this.modalService.create({
      nzTitle: 'მომხმარებლის შექმნა',
      nzContent: AddEmployeeAppointmentUserComponent,
      nzWidth: '900px',
      nzStyle: { top: '0px' },
      nzData: {
        userId: this.form.value.userId,
      },
      nzOnOk: () => {},
    });

    modal.afterClose.pipe(takeUntil(this.sub$)).subscribe((res) => {
      console.log(res);
      this.form.get('userId')?.setValue(res.id);
      this.selectedCustomer = res;
    });
  }

  submit(): void {
    if (this.form.invalid) return;
    if (this.bookingId) {
      this.store
        .dispatch(new EditEmployeeAppointment(this.form.value))
        .pipe(takeUntil(this.sub$))
        .subscribe((res) => {
          setTimeout(() => this.modal.close(res), 300);
        });
    } else {
      this.store
        .dispatch(new AddEmployeeAppointment(this.form.value))
        .pipe(takeUntil(this.sub$))
        .subscribe((res) => {
          this.modal.close(res);
        });
    }
  }

  viewUser() {}
}
